import { NativeButton } from '@squareup/dex-ui';
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import { Markdown } from '@squareup/dex-ui-markdown';
import { ApiReferenceFieldName } from '@squareup/dex-ui-shared-api-reference';
import { MarketDropdown, MarketPopover } from '@squareup/dex-ui-shared-market';
import clsx from 'clsx';
import React, { FC, useEffect, useRef, useState } from 'react';

import styles from './property-info.module.css';

interface PropertyNameProps {
  description: string | undefined;
  name: string;
  showTooltip: boolean;
}

const PropertyName: FC<PropertyNameProps> = ({
  description,
  name,
  showTooltip,
}) => {
  const [isServer, setIsServer] = useState(true);
  const dropdownRef = useRef<globalThis.HTMLMarketDropdownElement>(null);
  const popoverRef = useRef<globalThis.HTMLMarketPopoverElement>(null);
  const [isDropdownLoaded, onDropdownRef] =
    useIsMarketComponentLoaded(dropdownRef);
  const [isPopoverLoaded, onPopoverRef] =
    useIsMarketComponentLoaded(popoverRef);

  useEffect(() => {
    setIsServer(false);
  }, []);

  const fieldName = (
    <ApiReferenceFieldName
      className={clsx(showTooltip && styles['underline'])}
      name={name}
    />
  );

  // use a non-interactive field name for SSR, or if no tooltip is needed
  if (isServer || !showTooltip || !description) {
    return fieldName;
  }

  const isMarketLoaded = isDropdownLoaded && isPopoverLoaded;

  return (
    <>
      {/* Prevent flicker by showing fieldname as placeholder until market is hydrated */}
      {!isMarketLoaded && fieldName}
      <MarketDropdown
        className={clsx(!isMarketLoaded && styles.hidden)}
        data-testid="property-name-tooltip"
        interaction="click"
        popoverPlacement="top-start"
        ref={onDropdownRef}
      >
        <NativeButton
          className={styles['trigger']}
          trackingId={`property-name-tooltip-${name}`}
          slot="trigger"
        >
          {fieldName}
        </NativeButton>
        <MarketPopover
          className={styles['property-desc-popover']}
          data-testid="property-description-popover"
          ref={onPopoverRef}
          slot="popover"
        >
          <Markdown
            markdown={description}
            textVariant="20"
            testId="property-description-popover-content"
          />
        </MarketPopover>
      </MarketDropdown>
    </>
  );
};

export { PropertyName };
